import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Box, SxProps, Theme } from '@mui/material';

/**
 * Eases the wrapper to its content height so swapping the phone/email form glides
 * instead of snapping. Sole height animator on the form — nothing measuring nests
 * inside (forgot-password fades via opacity + leaves flow), since two nested
 * measure-then-animate loops stutter against each other.
 *
 * `flow-root` contains BackrsForm's top margin (else the button clips) and anchors
 * the absolutely-placed forgot-password row. Overflow clips only mid-transition so a
 * focused field's border isn't cropped at rest.
 *
 * Not `AnimatedCollapse`: that measures in rAF (post-layout), reintroducing the
 * button flash; this measures synchronously pre-paint by design.
 */
export const AnimatedHeight = ({ children }: { children: ReactNode }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const heightRef = useRef<number | 'auto'>('auto');
  const [height, setHeight] = useState<number | 'auto'>('auto');
  const [isAnimating, setIsAnimating] = useState(false);

  const measure = useCallback(() => {
    const el = contentRef.current;

    if (!el) {
      return;
    }

    const next = el.scrollHeight;

    if (next === heightRef.current) {
      return;
    }

    // Reduced motion fires no onTransitionEnd, so flipping isAnimating on would clip
    // overflow forever. Skip the first measure (heightRef 'auto') — no mount animation.
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion && typeof heightRef.current === 'number') {
      setIsAnimating(true);
    }

    heightRef.current = next;
    setHeight(next);
  }, []);

  // Measure pre-paint after every render so a tab swap locks the height target before
  // the browser paints — otherwise the new layout flashes at its final size first.
  useLayoutEffect(() => {
    measure();
  });

  // Catch layout changes that don't come from a re-render (font load, async content).
  useEffect(() => {
    const el = contentRef.current;

    if (!el) {
      return;
    }

    const observer = new ResizeObserver(() => measure());

    observer.observe(el);

    return () => observer.disconnect();
  }, [measure]);

  return (
    <Box sx={animatedHeightSx(height, isAnimating)} onTransitionEnd={() => setIsAnimating(false)}>
      <Box ref={contentRef} sx={{ display: 'flow-root', position: 'relative' }}>
        {children}
      </Box>
    </Box>
  );
};

const animatedHeightSx =
  (height: number | 'auto', isAnimating: boolean): SxProps<Theme> =>
  () => ({
    width: '100%',
    height,
    overflow: isAnimating ? 'hidden' : 'visible',
    transition: 'height 0.25s ease',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  });
